import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, InputGroup } from '@components/base';
import { motion, useAnimation } from 'framer-motion';
import NextLink from 'next/link';
import { Wave } from '@components/common/svgs/WaveSVG';
import { ChevronRightIcon } from '@heroicons/react/solid';
import { useRouter } from 'next/router';
import { useMutation, useQueryClient } from 'react-query';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { refreshClient } from '@graphql/graphql-client/graphClient';
import { FacebookSVG } from '@components/common/svgs/FacbookSVG';
import { GoogleSVG } from '@components/common/svgs/GoogleSVG';
import { SnapchatSVG } from '@components/common/svgs/SnapchatSVG';
import { EyeconSVG } from '@components/common/svgs/EyeconSVG';
import { EyeconClosedSVG } from '@components/common/svgs/EyeconClosedSVG';
import { ErrorAnnouncement } from '@components/common/error/ErrorAnnouncement';
import { SkipperTextLogo } from '@components/common/header/SkipperTextLogo';
import { encodeQueryData } from '@utils/encodeQueryData';
import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';
import { TitleAndTags } from '@skipper/ui';

const signInValidation = yup.object().shape({
    email: yup.string().required('Email or Username is required'),
    password: yup.string().required('Password is required'),
    rememberMe: yup.bool(),
});

type FormikConfig = typeof useFormik;

function handleError<T extends ReturnType<FormikConfig>>(fieldName: string, formik: T): string {
    const error =
        formik.touched[fieldName] && formik.errors[fieldName]
            ? formik.errors[fieldName]
            : undefined;
    return error as string;
}

function groupErrors(...mutateError: { isError: boolean; errors: any }[]) {
    let hasError = false;
    const errorsList: string[] = [];
    mutateError.forEach((e) => {
        if (e.isError) {
            hasError = true;
            errorsList.push(e.errors);
        }
    });
    return { hasError, errorsList };
}

const AUTH_URL = process.env.NEXT_PUBLIC_AUTHORIZATION_URL;

export default function Signin() {
    const [showPassword, setShowPassword] = useState(false);
    const { dispatchNotification } = useUniversalModalDispatch();
    const [hover, setHover] = useState(false);
    const router = useRouter();
    const queryClient = useQueryClient();
    const [emailServerError, setEmailServerError] = useState<string | undefined>();
    const [passwordServerError, setPasswordServerError] = useState<string | undefined>();
    const { error } = router.query;

    useEffect(() => {
        if (router.query.message) {
            const message = Array.isArray(router.query.message)
                ? router.query.message?.[0]
                : router.query.message;
            if (message.toLowerCase() === 'success') {
                router.replace('/');
            }
        } else if (router.query.notice) {
            dispatchNotification({
                title: router.query.notice.toString(),
                text: '',
                type: 'success',
            });
            router.replace(router.pathname, undefined, { shallow: true });
        }
    }, [router]);

    const mutate = useMutation(
        async ({
            email,
            password,
            rememberMe,
        }: {
            email: string;
            password: string;
            rememberMe: boolean;
        }) => {
            const body = JSON.stringify({
                email,
                password,
                rememberMe,
            });
            const resp = await fetch('/api/signin', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body,
            });
            const jbody = await resp.json();
            if (resp.ok) {
                return jbody;
            }
            throw jbody;
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries(['session']);
            },
            onSuccess: (data) => {
                router.push('/');
            },
            onError: ({ errors }) => {
                const er = errors?.[0]?.message ?? 'something went wrong';
                if (er === 'Invalid email or password') {
                    setEmailServerError('Invalid email or username');
                    setPasswordServerError('or invalid password');
                }
            },
        },
    );

    const signinOauth = useMutation(
        async ({
            type,
            provider,
            params = {},
        }: {
            type: 'signin' | 'callback';
            provider: 'google' | 'facebook' | 'snapchat';
            params?: { [key: string]: string };
        }) => {
            const queryString = encodeQueryData({
                ...params,
                callbackUrl: '/auth/signin',
            });

            const response = await fetch(`${AUTH_URL}/api/auth/${type}/${provider}${queryString}`, {
                method: 'POST',
                mode: 'no-cors',
                redirect: 'follow',
            });

            const jsonResponse = await response.json();
            if (!response.ok) {
                throw jsonResponse;
            }
            return jsonResponse;
        },
        {
            onSettled: (data) => {
                if (data?.authUrl) {
                    window.location.href = data.authUrl;
                }
            },
            onError: () => {
                dispatchNotification({
                    title: 'OAuth Provider Error',
                    text: 'Unable to signin with provider',
                    type: 'danger',
                });
            },
        },
    );

    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
            rememberMe: true,
        },
        onReset: () => {
            setEmailServerError(undefined);
            setPasswordServerError(undefined);
        },
        onSubmit: ({ email, password, rememberMe }) => {
            setEmailServerError(undefined);
            setPasswordServerError(undefined);
            refreshClient();
            mutate.mutate({
                email: email.trim(),
                password: password.trim(),
                rememberMe,
            });
        },
        validationSchema: signInValidation,
    });
    const container = useRef<HTMLDivElement | null>(null);
    const Eyecon = (
        <div
            className="absolute right-0 top-0 bottom-0 flex items-center pr-4"
            onClick={() => setShowPassword((s) => !s)}>
            {showPassword ? (
                <EyeconSVG className="fill-current text-gray-800" />
            ) : (
                <EyeconClosedSVG className="absolute right-0 top-0 bottom-0" />
            )}
        </div>
    );
    const controls = useAnimation();

    useEffect(() => {
        if (hover) {
            controls.start({
                width: '100%',
            });
        } else {
            controls.start({
                width: '0%',
            });
        }
        return () => controls.stop();
    }, [hover, controls]);

    const emailError = emailServerError || handleError('email', formik as any);
    const passwordError = passwordServerError || handleError('password', formik as any);

    const { hasError, errorsList } = groupErrors(
        {
            isError: mutate.isError,
            errors: mutate.error?.errors?.map(
                (m: { message: string }) => m?.message,
            ) as unknown as string[],
        },
        {
            isError: !!error,
            errors: Array.isArray(error) ? error : [error],
        },
    );
    return (
        <div className="relative h-screen overflow-y-auto">
            <TitleAndTags title="Login" />
            <header className="pt-10 px-10">
                <div className="flex items-center justify-between px-20">
                    <SkipperTextLogo href="https://skipper.io" />
                </div>
            </header>
            <div className="relative pt-10 h-full ">
                <div className="z-10">
                    <div className="flex justify-center">
                        <div className="retlative px-20 py-12 bg-white rounded-sm z-10 shadow-md border border-gray-100 w-full max-w-xl">
                            <div>
                                <h1 className="text-5xl text-passport font-inter font-bold mb-4 text-center">
                                    Log into Skipper!
                                </h1>
                            </div>
                            <div className="text-center mt-4 text-base text-hardgray font-bold font-inter">
                                Log in with
                            </div>
                            <div className="mt-4 mb-6">
                                <button
                                    type="button"
                                    onClick={() =>
                                        signinOauth.mutate({ type: 'signin', provider: 'google' })
                                    }
                                    className="w-full mx-auto flex justify-center col-span-1 text-center border-gray-300 border px-4 py-4 rounded-sm hover:ring-2 hover:ring-passport cursor-pointer">
                                    <GoogleSVG />
                                </button>
                            </div>
                            <div className="relative flex justify-center items-center">
                                <div className="absolute left-0 right-0 h-0.5 bg-gray-300"></div>
                                <div className="bg-white py-1 px-4 z-10 text-base font-inter text-gray-500">
                                    or
                                </div>
                            </div>
                            {hasError && (
                                <ErrorAnnouncement title="Signin failed" list={errorsList} />
                            )}
                            <form onSubmit={formik.handleSubmit}>
                                <div className="mt-6">
                                    <InputGroup
                                        id="email"
                                        name="email"
                                        labelText={'Email or Username'}
                                        labelClassName={`font-bold font-inter text-gray-400`}
                                        className={`mb-2 ${emailError ? 'border-red-600' : ''}`}
                                        onChange={formik.handleChange}
                                        value={formik.values.email}
                                    />
                                    <InputGroup
                                        type={`${showPassword ? 'text' : 'password'}`}
                                        id="password"
                                        name="password"
                                        labelText={'Password'}
                                        labelClassName={`font-bold font-inter text-gray-400`}
                                        className={`${passwordError ? 'border-red-600' : ''}`}
                                        rightIcon={Eyecon}
                                        onChange={formik.handleChange}
                                        value={formik.values.password}
                                    />
                                </div>
                                <div className="flex flex-wrap gap-y-2 items-center justify-start md:justify-between mt-1">
                                    <div className="pt-2 sm:pt-1 text-gray-400">
                                        <Checkbox
                                            setChecked={formik.handleChange}
                                            checked={formik.values.rememberMe}
                                            srLabel="remember me"
                                            id="rememberMe"
                                            label="Remember Me"
                                            labelBold=""
                                            labelClassName="text-gray-400 font-bold z-10"
                                            name="rememberMe"
                                        />
                                    </div>
                                    <NextLink href="/auth/password/new" passHref>
                                        <a className="text-gray-400 font-bold hover:text-passport z-10 cursor-pointer">
                                            Forgot your password?
                                        </a>
                                    </NextLink>
                                </div>
                                <Button
                                    variant="secondary"
                                    size="large"
                                    type="submit"
                                    className="w-full text-center rounded-full flex items-center justify-center mt-6"
                                    disabled={mutate.isLoading}>
                                    <p className="text-xl font-inter font-bold">{`${
                                        mutate.isLoading ? 'Signing In...' : 'Log in'
                                    }`}</p>
                                </Button>
                            </form>
                            <div className="flex flex-col items-center mt-6">
                                <p className="text-base text-hardgray font-bold font-inter">
                                    {"Don't have an account?"}
                                </p>

                                <a
                                    className="group text-lg text-magenta-600 flex items-center my-2"
                                    href="/auth/signup"
                                    rel="noreferrer">
                                    Create your free account
                                    <ChevronRightIcon className="-mb-1 group-hover:transform group-hover:translate-x-1 w-7 h-7" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="relative z-20 w-full flex items-center justify-center mt-5">
                    <div
                        ref={container}
                        className="group relative "
                        onMouseEnter={() => setHover(true)}
                        onMouseLeave={() => setHover(false)}>
                        <a
                            href="https://skipper.io/privacy"
                            target="_blank"
                            rel="noreferrer"
                            className="block z-20 text-teal-600 font-bold">
                            View our Privacy Policy
                        </a>
                        <motion.div animate={controls} layout>
                            <div className="w-full h-1 bg-teal-400" />
                        </motion.div>
                    </div>
                </div>
                <Wave />
            </div>
        </div>
    );
}
